<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment scheduler webpage -->
<template>
  <div class="appointment-scheduler">
    <!-- Form -->
    <div class="form" v-if="step < 5">
      <!-- Selecting Appointment Type -->
      <section class="select-appointment-type">
        <!-- Step 1 - Select sponsor -->
        <h3 :class="{ inactive: step !== 1}">
          <span class="step-ind">1</span> Select Sponsor
        </h3>
        <p class="sponsor-select-message" v-if="scheduleForm.sponsorId === 0">If the appointment request is a Letter of Intent, White Paper or Pre-Proposal please select Letter of Intent/ White Paper / Pre-Proposal from the dropdown.</p>

        <av-spinner v-if="loadingSponsors" class="loading-sponsors">Loading Sponsors</av-spinner>

        <div class="sponsor-selection" v-if="step === 1 && scheduleForm.sponsorId === 0 && !loadingSponsors">
          <div class="sponsor-search-field">
            <input type="text" v-model="sponsorsSearchKey" placeholder="Search" />
          </div>
          <ul>
            <li v-for="sponsor of filteredSponsors" :key="sponsor.id" @click="selectSponsor(sponsor)">{{ sponsor.name }}</li>
            <li v-if="filteredSponsors.length === 0" class="no-results">No Results</li>
          </ul>
        </div>

        <div v-if="!internalScheduling && step === 1" class="warning-message">
          <i>*Please contact OSP at 505-277-4186 or via email at osp@unm.edu if the sponsor is not listed.</i>
        </div>

        <div v-if="scheduleForm.sponsorId !== 0" class="appointment-type-selection selected-step">
          <h5>{{ selectedSponsor.name }}</h5>
        </div>

        <div v-if="internalScheduling && scheduleForm.sponsorId !== 0 && step === 1" class="appointment-type-list">
          <div
            v-if="sponsorAppointmentType"
            class="appointment-type-selection"
            @click="selectAppointmentType(sponsorAppointmentType.id)"
          >
            <h5>{{ sponsorAppointmentType.name }}</h5>
            <span class="duration"><i class="fal fa-clock"></i> {{ formatMinutes(sponsorAppointmentType.duration) }}</span>
            <p class="description">{{ sponsorAppointmentType.description }}</p>
          </div>
          <div
            v-for="appointmentType of taskAppointmentTypes"
            :key="appointmentType.id"
            class="appointment-type-selection"
            @click="selectAppointmentType(appointmentType.id)"
          >
            <h5>{{ appointmentType.name }}</h5>
            <span class="duration"><i class="fal fa-clock"></i> {{ formatMinutes(appointmentType.duration) }}</span>
            <p class="description">{{ appointmentType.description }}</p>
          </div>
        </div>

        <div v-if="step > 1 && !showSponsors" class="appointment-type-selection selected-step">
          <h5>{{ selectedAppointmentType.name }}</h5>
        </div>

        <div class="actions" v-if="step === 1 && scheduleForm.sponsorId !== 0">
          <av-button @click="back" class="back-btn hollow dark-gray">Back</av-button>
        </div>

        <div class="actions" v-if="step === 1 && !internalScheduling">
          <av-button @click="cancel" class="back-btn hollow dark-gray">Back</av-button>
        </div>
      </section>

      <!-- Step 2 - Selecting Deadline -->
      <transition name="reveal-step">
      <section class="select-deadline" v-if="step >= 2">
        <h3 :class="{ inactive: step !== 2}">
          <span class="step-ind">2</span> Proposal Deadline
        </h3>
        <p class="warning-message" v-if="step === 2">*If you are requesting an appointment for a proposal with a soft-deadline, please select a deadline date two-days ahead of the desired review date.</p>
        <br>
        <div class="deadline-picker" v-if="step === 2"></div>
        <div class="selected-date selected-step" v-if="scheduleForm.reviewDeadline">
          <h5>{{ formatTime(scheduleForm.reviewDeadline, 'MMM do yyyy') }}</h5>
        </div>

        <div class="actions" v-if="step === 2">
          <av-button @click="back" class="back-btn hollow dark-gray">Back</av-button>
        </div>
      </section>
      </transition>

      <transition name="reveal-step">
        <div v-if="(hasReviewDeadlinePassed || isReviewDeadlineSameDay) && !internalScheduling">
          <section v-if="hasReviewDeadlinePassed && !isReviewDeadlineSameDay" class="deadline-has-passed">
            <h3>Your Deadline Has Passed</h3>
            <p>Comprehensive Component Reviews cannot be scheduled after proposal deadline dates. Please contact ospapppintment@unm.edu to setup an Essential Component Review</p>
          </section>

          <section v-if="isReviewDeadlineSameDay" class="deadline-has-passed">
            <h3>Cannot Schedule On Day Of Deadline</h3>
            <p>Comprehensive Component Reviews cannot be scheduled on proposal deadline dates. Please contact ospapppintment@unm.edu to setup an Essential Component Review</p>
          </section>

          <div class="actions">
            <av-button @click="back" class="back-btn hollow dark-gray">Back</av-button>
          </div>
        </div>
      </transition>

      <!-- Step 3 - Select Appointment Time -->
      <transition name="reveal-step">
      <section class="select-time" v-if="step >= 3 && (!hasReviewDeadlinePassed || internalScheduling) && !selectedAppointmentType.isTask">
        <h3 :class="{ inactive: step !== 3}">
          <span class="step-ind">3</span> Select Appointment Time
        </h3>

        <template v-if="step === 3 && !scheduleForm.startTime">
          <appointment-slot-selection
            :appointmentTypeId="scheduleForm.appointmentTypeId"
            :internalScheduling="internalScheduling"
            :forReschedule="false"
            :currentReviewerId="0"
            :maxDate="scheduleForm.reviewDeadline"
            @slot-selected="selectSlot($event)"
          ></appointment-slot-selection>
        </template>

        <p v-if="step === 3 && !internalScheduling" class="warning-message">*If there is no availability please contact OSP at 505-277-4186 or via email at osp@unm.edu</p>

        <div v-if="step > 3" class="selected-step">
          {{ formatTime(scheduleForm.startTime, 'MMMM do yyyy \'at\' h:mma') }}
        </div>

        <p v-if="isSixMonthsOut(scheduleForm.startTime)" class="warning-message"><i>You have selected an appointment time that is 6 months or more out from today's date.  Please confirm all information is correct before proceeding.</i></p>

        <div v-if="internalScheduling && scheduleForm.startTime && step === 3" class="select-reviewer">
          <!-- Select reviewer if scheduling by reviewer -->
          <h5>Select Reviewer for {{ formatTime(scheduleForm.startTime, 'MMMM do yyyy \'at\' h:mma') }}</h5>
          <div v-if="availableReviewerExists" class="reassign-selection" @click="selectReviewer(null)">
            <div class="reviewer-info">
              <span>Next Available Reviewer</span>
            </div>
          </div>
          <div class="reassign-selection" v-for="reviewer of sortedAvailableReviewers" :key="reviewer.id" @click="selectReviewer(reviewer.id)">
            <div class="reviewer-info">
              <span>{{ reviewer.firstName + ' ' + reviewer.lastName }}</span>
              <span class="workload-ind">{{ workloadInHours(reviewer.workload) }}</span>
            </div>
            <div class="reassign-warnings">
              <p class="conflicts" v-if="reviewer.hasExternalConflicts"><i class="fal fa-exclamation-triangle"></i> This reviewer has conflicts at this time.</p>
              <p class="no-availability" v-if="!reviewer.availableForSlot && !selectedAppointmentType.isTask"><i class="fal fa-exclamation-circle"></i> This reviewer does not have regular availability for this review and time.</p>
            </div>
          </div>
        </div>

        <div class="actions" v-if="step === 3">
          <av-button v-if="!scheduleForm.startTime" @click="back" class="back-btn hollow dark-gray">Back</av-button>
          <av-button v-if="scheduleForm.startTime" @click="backToTime" class="back-btn hollow dark-gray">Back</av-button>
        </div>
      </section>
      </transition>

      <!-- Select Task Date -->
      <transition name="reveal-step">
      <section class="select-deadline" v-if="step >= 3 && internalScheduling && selectedAppointmentType.isTask">
        <!-- Select review date -->
        <h3 :class="{ inactive: step !== 3}">
          <span class="step-ind">3</span> Select Review Date {{ internalScheduling ? '& Reviewer' : '' }}
        </h3>
        <div class="review-date-picker" v-if="step === 3"></div>
        <div class="selected-date selected-step" v-if="scheduleForm.start">
          <h5>{{ formatTime(scheduleForm.start, 'MMM do yyyy') }}</h5>
        </div>

        <div v-if="internalScheduling && scheduleForm.startTime && step === 3" class="select-reviewer">
          <!-- Select reviewer if scheduling by reviewer -->
          <h5>Select Reviewer</h5>
          <div class="reassign-selection" v-for="reviewer of sortedAvailableReviewers" :key="reviewer.id" @click="selectReviewer(reviewer.id)">
            <div class="reviewer-info">
              <span>{{ reviewer.firstName + ' ' + reviewer.lastName }}</span>
              <span class="workload-ind">{{ workloadInHours(reviewer.workload) }}</span>
            </div>
            <div class="reassign-warnings">
              <p class="conflicts" v-if="reviewer.hasExternalConflicts"><i class="fal fa-exclamation-triangle"></i> This reviewer has conflicts at this time.</p>
            </div>
          </div>
        </div>

        <div v-if="step > 3" class="selected-step">
          {{ formatTime(scheduleForm.startTime, 'MMMM do yyyy') }}
        </div>

        <div class="actions" v-if="step === 3">
          <av-button @click="back" class="back-btn hollow dark-gray">Back</av-button>
        </div>
      </section>
      </transition>

      <!-- Step 4 - Intake Form -->
      <transition name="reveal-step">
      <section class="intake-form" v-if="step === 4">
        <!-- Form description -->
        <h3>
          <span class="step-ind">4</span> Information
        </h3>

        <intake-form
          :intakeForm="selectedAppointmentType.currentForm"
          @form-updated="scheduleForm.formResponse = $event"
        ></intake-form>

        <div class="actions">
          <av-button v-if="step > 1" @click="back" class="back-btn hollow dark-gray">Back</av-button>
          <av-button @click="goToConfirm" :disabled="!scheduleForm.formResponse.isValid">Next <i class="fal fa-arrow-right"></i></av-button>
        </div>
      </section>
      </transition>
    </div>

    <!-- Last step - Confirmation Page -->
    <transition name="reveal-step">
    <div class="confirm" v-if="step === 5">
      <section>
        <h3><span class="step-ind">4</span> Confirm</h3>
        <h4>{{ selectedAppointmentType.name }}</h4>
        <p class="description">{{ selectedAppointmentType.description }}</p>
        <div class="date-time">
          <div class="date"><i class="fal fa-calendar"></i> {{ formatTime(scheduleForm.startTime, 'MMM do yyyy') }}</div>
          <div class="time" v-if="!selectedAppointmentType.isTask"><i class="fal fa-clock"></i> {{ formatTime(scheduleForm.startTime, 'h:mma')}} - {{ formatTime(scheduleForm.endTime, 'h:mma') }}</div>
        </div>

        <div class="responses">
          <div class="pi-name">
            <div><p><label class="av-label">P.I. First Name</label> {{ scheduleForm.formResponse.defaultResponses['pIFirstName'].value }}</p></div>
            <div><p><label class="av-label">P.I. Last Name</label> {{ scheduleForm.formResponse.defaultResponses['pILastName'].value }}</p></div>
          </div>

          <div class="pi-name">
            <div><p><label class="av-label">P.I. Phone</label> {{ scheduleForm.formResponse.defaultResponses['pIPhone'].value }}</p></div>
            <div><p><label class="av-label">P.I. Email</label> {{ scheduleForm.formResponse.defaultResponses['pIEmail'].value }}</p></div>
          </div>

          <div class="additional-contacts" v-if="scheduleForm.formResponse.additionalContacts.length > 0">
            <label class="av-label">Additional Contacts</label>
            <p class="additional-contact" v-for="contact of scheduleForm.formResponse.additionalContacts" :key="contact">
              <span>{{ contact.name }},</span>
              <span>{{ contact.phone }},</span>
              <span>{{ contact.email }}</span>
            </p>
          </div>

          <p v-for="response of scheduleForm.formResponse.intakeFormResponses" :key="response.label">
            <label class="av-label">{{ response.label }}</label>
            {{ response.value }}
          </p>
        </div>

        <div class="actions">
          <av-button @click="back" class="back-btn hollow dark-gray" :disabled="scheduling">Back</av-button>
          <av-button @click.once="schedule" :disable="!isActive" :loading="scheduling"><i class="fal fa-calendar-plus"></i> Schedule</av-button>
        </div>
      </section>
    </div>
    </transition>

    <transition name="reveal-step">
      <div class="confirmation" v-if="step === 6">
        <i class="fal fa-check-circle"></i>
        <br>
        <span>Your appointment has been scheduled.</span>
      </div>
    </transition>

    <av-modal :visible="timeNotAvailable" :canCloseSelf="false">
      <div class="modal-header">
        Appointment Time Unavailable
      </div>
      <div class="modal-content">
        Unfortuately, the appointment time you selected is no longer available.
      </div>
      <div class="modal-actions">
        <a @click="timeNotAvailable = false; cancel()" class="shaded red">Back To Home</a>
        <a @click="timeNotAvailable = false; step = 3" class="shaded black">Select New Time</a>
      </div>
    </av-modal>

    <av-modal :visible="errorScheduling">
      <div class="modal-header">
        Could Not Schedule
      </div>
      <div class="modal-content">
        Unfortuately, there was an error scheduling your appointment. Please try again. If the problem persists, please contact us at osp@unm.edu.
      </div>
      <div class="modal-actions">
        <a @click="cancel" class="shaded red">OK</a>
      </div>
    </av-modal>
  </div>
</template>

<script lang="ts">
import { addDays, addMinutes, addMonths, addWeeks, differenceInMilliseconds, endOfDay, endOfMinute, endOfWeek, format, isAfter, isBefore, isSameDay, startOfDay, startOfWeek } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import AppointmentService from '../common/appointmentService'
import TimeService from '../common/timeService'
import { AppointmentType, AppointmentTypeDetail } from '../models/appointmentType'
import AvailabilitySlot from '../models/availabilitySlot'
import { DynamicInput } from '../models/dynamicInput'
import DynamicField from '../components/DynamicField.vue'
import { cloneDeep } from 'lodash'
import AppointmentSlotSelection from './AppointmentSlotSelection.vue'
import SponsorService from '../common/sponsorService'
import { Sponsor } from '../models/sponsor'
import User from '../models/user'
import UserReassign from '../models/userReassign'
import { mapGetters, mapState } from 'vuex'
import { AxiosError } from 'axios'
import IntakeForm from './IntakeForm.vue'
import FormattedIntakeFormResponse from '../models/formattedIntakeFormResponse'

export default defineComponent({
  name: 'AppointmentScheduler',

  props: {
    /** Indicates that a reviewing is scheduling an appointment */
    internalScheduling: {
      type: Boolean,
      default: false
    }
  },

  emits: ['scheduled', 'cancel'],

  data () {
    return {
      isActive: true,

      errorScheduling: false,

      timeNotAvailable: false,

      loadingSponsors: false,

      /** List of available appointment types */
      appointmentTypes: [] as Array<AppointmentTypeDetail>,

      /** List of available times for the selected appointment type */
      // availabilitySlots: [] as Array<AvailabilitySlot>,

      /** List of reviewers for a given time slot */
      availableReviewers: [] as Array<UserReassign>,

      /** Date range to pull availability */
      dateRange: {
        endTime: endOfWeek(new Date()),
        startTime: startOfWeek(new Date())
      },

      /** Information collected to schedule the appointment */
      scheduleForm: {
        appointmentTypeId: 0,
        appointmentTypeOfferingId: 0,
        sponsorId: 0,
        sponsorName: '',
        endTime: null as unknown as Date,
        formResponse: {} as FormattedIntakeFormResponse,
        startTime: null as unknown as Date,
        reviewDeadline: null as unknown as Date,
        reviewerId: null as unknown as number | null
      },

      /** Indicates that the appointment is being scheduled */
      scheduling: false,

      /** Indicates that appointment type will be selected through sponsor */
      showSponsors: true,

      /** List of sponsors */
      sponsors: [] as Array<Sponsor>,

      /** String to search sponsors ons */
      sponsorsSearchKey: '',

      /** The current step of the schedule process */
      step: 1
    }
  },

  mounted () {
    // Get sponsors
    this.loadingSponsors = true
    SponsorService.getAll().then(res => {
      this.sponsors = res.data.sort((a, b) => {
        if (a.toTop > b.toTop) {
          return -1
        } else if (a.toTop < b.toTop) {
          return 1
        } else {
          return 0
        }
      })

      this.loadingSponsors = false
    })

    // Get all appointment types
    if (this.internalScheduling) {
      AppointmentTypeService.getAll().then(res => {
        this.appointmentTypes = res.data.filter(r => !r.removed)
      })
    } else {
      AppointmentTypeService.getPublic().then(res => {
        this.appointmentTypes = res.data
      })
    }
  },

  computed: {
    /** get appointment type for selected sponsor */
    sponsorAppointmentType (): AppointmentTypeDetail {
      return this.appointmentTypes.find(at => at.id === this.selectedSponsor.appointmentTypeId) as AppointmentTypeDetail
    },

    /** returns true if appointment type is a task */
    taskAppointmentTypes (): Array<AppointmentType> {
      return this.appointmentTypes.filter(at => at.isTask)
    },

    /** sort available reviewers by workload */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      if (this.isAdmin) {
        return reviewers
      } else {
        return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
      }
    },

    /** Available Reviewer */
    availableReviewerExists (): boolean {
      if (!this.availableReviewers) {
        return false
      }

      return this.availableReviewers.some(u => !u.hasExternalConflicts && u.availableForSlot)
    },

    ...mapState(['userId']),

    ...mapGetters(['isAdmin']),

    /** filter sponsor listed down to characters typed */
    filteredSponsors (): Array<Sponsor> {
      if (this.sponsorsSearchKey !== '') {
        return this.sponsors.filter(s => RegExp(this.sponsorsSearchKey, 'i').test(s.name))
      } else {
        return this.sponsors
      }
    },

    /** Returns true if the deadline has already passed */
    hasReviewDeadlinePassed (): boolean {
      return isBefore(this.scheduleForm.reviewDeadline, new Date())
    },

    /** returns true if review deadline is today */
    isReviewDeadlineSameDay (): boolean {
      return isSameDay(new Date(), this.scheduleForm.reviewDeadline)
    },

    /** The selected appointment type */
    selectedAppointmentType (): AppointmentTypeDetail {
      return this.appointmentTypes.find(at => at.id === this.scheduleForm.appointmentTypeId) as AppointmentTypeDetail
    },

    /** The selected sponsor */
    selectedSponsor (): Sponsor {
      return this.sponsors.find(s => s.id === this.scheduleForm.sponsorId) as Sponsor
    },

    /** The slots that fall within the current time range */
    // slotsForWeek (): Array<AvailabilitySlot> {
    //   return this.availabilitySlots.filter(slot => {
    //     return isAfter(slot.start, this.dateRange.startTime) && isBefore(slot.start, this.dateRange.endTime)
    //   })
    // },

    /** Dates for the current week */
    weekDays (): Array<Date> {
      return [1, 2, 3, 4, 5].map(x => {
        return addDays(this.dateRange.startTime, x)
      })
    }
  },

  methods: {
    /** cancel action */
    cancel () {
      this.$emit('cancel')
    },

    /** returns true if given date is six months or more out */
    isSixMonthsOut (date: Date) {
      return isAfter(date, addMonths(new Date(), 6))
    },

    /** sets reviewer chosen */
    selectReviewer (userId: number | null) {
      this.scheduleForm.reviewerId = userId
      this.step = 4
    },

    /**
     * workload in hours conversion
     */
    workloadInHours (workload: number): string {
      const hours = Math.floor(workload / 60)
      const minutes = workload - (hours * 60)
      return `${hours > 0 ? hours : '0'}${hours === 1 ? 'hr' : 'hrs'} ${minutes}min`
    },

    /**
     * Moves the form back one step
     */
    back () {
      if (this.step === 1) {
        this.step += 1
        this.scheduleForm.sponsorId = 0
      } else if (this.step === 2) {
        if (this.internalScheduling) {
          this.scheduleForm.appointmentTypeId = 0
        } else {
          this.scheduleForm.appointmentTypeId = 0
          this.scheduleForm.sponsorId = 0
        }
      } else if (this.step === 3) {
        this.showDeadlineCalendar()
        this.scheduleForm.reviewDeadline = null as unknown as Date
        // this.availabilitySlots = []
      } else if (this.step === 4) {
        this.scheduleForm.appointmentTypeOfferingId = 0
        this.scheduleForm.startTime = null as unknown as Date
        this.scheduleForm.endTime = null as unknown as Date
        if (this.selectedAppointmentType.isTask) {
          this.showReviewDateCalendar()
        }
      }

      this.step -= 1
    },

    backToTime () {
      this.scheduleForm.startTime = null as unknown as Date
      this.scheduleForm.endTime = null as unknown as Date
      this.availableReviewers = []
    },

    /**
     * Formates the given number of minutes into a human readable time
     */
    formatMinutes (minutes: number): string {
      return TimeService.minutesToHoursFormatted(minutes)
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * Fetches availability for the selected appointment type
     * and date range
     */
    // getAvailability () {
    //   const start = new Date()
    //   const end = this.scheduleForm.reviewDeadline
    //   AppointmentTypeService.getAvailability(this.scheduleForm.appointmentTypeId, start, end, false).then(res => {
    //     res.data.forEach(d => {
    //       d.start = new Date(d.start)
    //       d.end = new Date(d.end)
    //     })

    //     this.dateRange.startTime = startOfWeek(res.data[0].start)
    //     this.dateRange.endTime = endOfWeek(this.dateRange.startTime)

    //     this.availabilitySlots = res.data
    //   })
    // },

    /**
     * returns true if user can go to confirmation page
     */
    goToConfirm () {
      const el = document.querySelector('.system-settings.page')
      if (el) {
        el.scrollTop = 0
      }

      this.step = 5
    },

    /**
     * Moves the date range forward one week
     */
    nextWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, 1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, 1)
    },

    /**
     * Moves the date range back one week
     */
    previousWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, -1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, -1)
    },

    /**
     * Schedules the appointment with the given information
     */
    schedule () {
      this.scheduling = true
      this.isActive = false

      const params = {
        appointmentTypeId: this.scheduleForm.appointmentTypeId,
        appointmentTypeOfferingId: this.scheduleForm.appointmentTypeOfferingId,
        startTime: this.scheduleForm.startTime.toISOString(),
        intakeFormResponse: this.scheduleForm.formResponse.intakeFormResponses,
        reviewDeadline: this.scheduleForm.reviewDeadline.toISOString(),
        reviewerId: this.scheduleForm.reviewerId,
        sponsorId: this.scheduleForm.sponsorId,
        projectUserId: this.userId,
        additionalContacts: this.scheduleForm.formResponse.additionalContacts
      }

      const globalResponses = cloneDeep(Object.values(this.scheduleForm.formResponse.defaultResponses))
      globalResponses.forEach(gr => {
        params[gr.id] = gr.value
      })

      AppointmentService.scheduleAppointment(params).then(res => {
        this.step = 6
        this.$emit('scheduled')
        this.scheduling = false
      }).catch((err: AxiosError) => {
        (window as any).bad = err
        if (err.response?.status === 409) {
          // this.getAvailability()
          this.scheduling = false
          this.timeNotAvailable = true
        } else {
          this.errorScheduling = true
        }
      })
      this.isActive = true
    },

    /**
     * Handles the selection of an appointment type
     * @param appointmentType: The selected appointment type
     */
    selectAppointmentType (appointmentTypeId: number) {
      this.scheduleForm.appointmentTypeId = appointmentTypeId
      this.showDeadlineCalendar()
      // this.resetIntakeFormResponse()
      this.step = 2
    },

    /**
     * Handles the selection of a time slot
     * @param slot: The time slot
     */
    selectSlot (slot: AvailabilitySlot) {
      this.scheduleForm.appointmentTypeOfferingId = slot.appointmentTypeOffering.id
      this.scheduleForm.startTime = slot.start
      this.scheduleForm.endTime = addMinutes(slot.start, this.selectedAppointmentType.duration)
      if (this.internalScheduling) {
        AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(res => {
          this.availableReviewers = res.data
        })
        // this.scheduleForm.reviewerId = slot.reviewer.id
      } else {
        this.step = 4
      }
    },

    /**
     * Selects the sponsor and sets the appointment
     * @params sponsor: The given sponsor
     */
    selectSponsor (sponsor: Sponsor) {
      this.scheduleForm.sponsorId = sponsor.id
      if (!this.internalScheduling) {
        this.selectAppointmentType(sponsor.appointmentTypeId)
      }
    },

    // /**
    //  * Returns the slots available on the given day
    //  * @param day: The day in the form of a Date object
    //  */
    // slotsForDay (day: Date): Array<AvailabilitySlot> {
    //   return this.slotsForWeek.filter(slot => {
    //     return slot.start.getDay() === day.getDay()
    //   })
    // },

    /** shows calendar to pick deadline date */
    showDeadlineCalendar () {
      setTimeout(() => {
        const picker = new Litepicker({ element: document.querySelector('.deadline-picker'), inlineMode: true, singleMode: true, onSelect: this.handleDeadlineSelection })
        picker.show()
      }, 0)
    },

    /** shows calendar for review date selection */
    showReviewDateCalendar () {
      setTimeout(() => {
        const picker = new Litepicker({ element: document.querySelector('.review-date-picker'), inlineMode: true, singleMode: true, onSelect: this.handleReviewDateSelection, disableWeekends: true, minDate: startOfDay(new Date()), maxDate: endOfDay(this.scheduleForm.reviewDeadline) })
        picker.show()
      }, 0)
    },

    /** handle reviewers available for date */
    handleReviewDateSelection (date: Date) {
      this.scheduleForm.startTime = startOfDay(date)
      this.scheduleForm.endTime = endOfDay(date)

      if (!this.internalScheduling) {
        this.step = 4
      } else {
        AppointmentService.reviewerAvailabilityForDate(date).then(res => {
          this.availableReviewers = res.data
        })
      }
    },

    /** handle date selection and deadline date */
    handleDeadlineSelection (date: Date) {
      this.scheduleForm.reviewDeadline = date
      if (!this.selectedAppointmentType.isTask) {
        // this.getAvailability()
      } else {
        this.showReviewDateCalendar()
      }
      this.step = 3
    }
  },

  components: {
    // DynamicField,
    IntakeForm,
    AppointmentSlotSelection
  }
})
</script>

<style>
.reveal-step-enter-active,
.reveal-step-leave-active {
  will-change: opacity transform;
  transition-duration: 0.4s;
  transition-property: opacity transform;
  transition-timing-function: ease-out;
}

.reveal-step-leave-active {
  transition-duration: 0.1s;
}

.reveal-step-enter-from {
  opacity: 0;
  transform: translateY(50px);
}

.reveal-step-leave-to {
  opacity: 0;
  transform: translateY(50px);
  transform-origin: top center;
}
</style>
